import React, { useState, useEffect, useCallback, useRef } from "react";
import { apiGet } from "../../../services/api";
import { applyChartMutationClient, type ChartMutationFrame } from "../toothchart/applyChartMutationClient";
import { exportElementToPdf } from "../../../utils/pdfExport";
import ChartBig from "../toothchart/ChartBig";
import "./ChartHistoryReplay.css";

interface ChartFrame {
  id?: string;
  symbol?: string;
  tooth?: string;
  tooth_number?: string;
  surface?: string;
  procedure?: string | { name?: string };
  step_key?: string;
  step_label?: string;
  timestamp?: number | string;
}

interface ReplayChartState {
  states: Record<string, string>;
  surfaces: Record<string, unknown>;
  flags: Record<string, unknown>;
  movements: Record<string, unknown>;
}

const emptyReplayState: ReplayChartState = {
  states: {},
  surfaces: {},
  flags: {},
  movements: {},
};

// Fit constants matching ChartHistoryPlayer / ChartBig usage
const REPLAY_FIT_S = 0.16;

export interface ChartHistoryReplayProps {
  visitId?: string | null;
  onFrameChange?: (payload: { state: ReplayChartState; frame: ChartFrame | null } | null) => void;
  initialChartState?: ReplayChartState;
}

export default function ChartHistoryReplay({ visitId, onFrameChange }: ChartHistoryReplayProps) {
  const [frames, setFrames] = useState<ChartFrame[]>([]);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1000); // ms per frame
  const [replayState, setReplayState] = useState<ReplayChartState>(emptyReplayState);
  const exportRef = useRef<HTMLDivElement | null>(null);

  const loadChartHistory = useCallback(async () => {
    if (!visitId) return;
    setLoading(true);
    try {
      const res = await apiGet(`/api/doctor/chart-history/${visitId}`);
      const loadedFrames = Array.isArray(res) ? (res as ChartFrame[]) : [];
      setFrames(loadedFrames);

      if (loadedFrames.length > 0) {
        setCurrentFrameIndex(loadedFrames.length - 1);
      } else {
        setCurrentFrameIndex(-1);
      }
    } catch (e) {
      console.error("Failed to load chart history", e);
      setFrames([]);
      setCurrentFrameIndex(-1);
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    if (!visitId) {
      setFrames([]);
      setCurrentFrameIndex(-1);
      setReplayState(emptyReplayState);
      return;
    }

    setIsPlaying(false);
    loadChartHistory();
  }, [visitId, loadChartHistory]);

  const rebuildFromFrames = useCallback((framesList: ChartFrame[], upToIndex: number): ReplayChartState => {
    const state: ReplayChartState = {
      states: {},
      surfaces: {},
      flags: {},
      movements: {},
    };

    for (let i = 0; i <= upToIndex && i < framesList.length; i++) {
      const f = framesList[i];
      applyChartMutationClient(state, f as unknown as ChartMutationFrame);
    }

    return state;
  }, []);

  const handleScrub = useCallback((index: number) => {
    const rebuilt = rebuildFromFrames(frames, index);
    setReplayState(rebuilt);
    setCurrentFrameIndex(index);

    if (onFrameChange) {
      onFrameChange({
        state: rebuilt,
        frame: frames[index] || null,
      });
    }
  }, [frames, rebuildFromFrames, onFrameChange]);

  // Rebuild whenever currentFrameIndex changes (initial load + playback)
  useEffect(() => {
    if (currentFrameIndex >= 0 && currentFrameIndex < frames.length) {
      const rebuilt = rebuildFromFrames(frames, currentFrameIndex);
      setReplayState(rebuilt);
      if (onFrameChange) {
        onFrameChange({
          state: rebuilt,
          frame: frames[currentFrameIndex],
        });
      }
    } else if (currentFrameIndex === -1) {
      setReplayState(emptyReplayState);
      if (onFrameChange) onFrameChange(null);
    }
  }, [currentFrameIndex, frames, onFrameChange, rebuildFromFrames]);

  // Auto-advance during playback
  useEffect(() => {
    if (!isPlaying || currentFrameIndex >= frames.length - 1) {
      setIsPlaying(false);
      return;
    }

    const timer = setTimeout(() => {
      setCurrentFrameIndex((prev) => Math.min(prev + 1, frames.length - 1));
    }, playbackSpeed);

    return () => clearTimeout(timer);
  }, [isPlaying, currentFrameIndex, frames.length, playbackSpeed]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(e.target.value, 10);
    handleScrub(index);
    setIsPlaying(false);
  };

  const goToFirst = () => { setCurrentFrameIndex(0); setIsPlaying(false); };
  const goToLast = () => { setCurrentFrameIndex(frames.length - 1); setIsPlaying(false); };
  const goToPrevious = () => { setCurrentFrameIndex((prev) => Math.max(0, prev - 1)); setIsPlaying(false); };
  const goToNext = () => { setCurrentFrameIndex((prev) => Math.min(frames.length - 1, prev + 1)); setIsPlaying(false); };

  const togglePlay = () => {
    if (currentFrameIndex >= frames.length - 1) {
      setCurrentFrameIndex(0);
    }
    setIsPlaying((p) => !p);
  };

  const currentFrame = currentFrameIndex >= 0 && currentFrameIndex < frames.length
    ? frames[currentFrameIndex]
    : null;

  const handleExportPdf = async () => {
    await exportElementToPdf(exportRef.current, { filename: "chart-history.pdf" });
  };

  const formatTime = (ts?: number | string) => {
    if (!ts) return "—";
    const d = new Date(ts as string | number);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDateTime = (ts?: number | string) => {
    if (!ts) return "—";
    const d = new Date(ts as string | number);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString([], { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" });
  };

  // ── Loading ───────────────────────────────────────────
  if (loading) {
    return (
      <div className="chart-replay-container">
        <div className="replay-loading">⏳ Loading chart history…</div>
      </div>
    );
  }

  // ── No frames ─────────────────────────────────────────
  if (frames.length === 0) {
    return (
      <div className="chart-replay-container">
        <div className="replay-empty">
          <span className="empty-icon">📊</span>
          <p>No chart history available</p>
          <small>Chart changes will appear here as procedures progress</small>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────
  return (
    <div className="chart-replay-container" ref={exportRef}>

      {/* ── Header ── */}
      <div className="replay-header">
        <div className="replay-title">
          <span className="icon">🎬</span>
          <strong>Chart History Replay</strong>
          <span className="frame-count">
            {currentFrameIndex + 1} / {frames.length}
          </span>
        </div>
        <div className="replay-speed-control">
          <label>Speed:</label>
          <select
            value={playbackSpeed}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPlaybackSpeed(Number(e.target.value))}
            disabled={isPlaying}
          >
            <option value={2000}>Slow (2s)</option>
            <option value={1000}>Normal (1s)</option>
            <option value={500}>Fast (0.5s)</option>
            <option value={200}>Very Fast (0.2s)</option>
          </select>
        </div>
        <button type="button" className="btn-export" onClick={handleExportPdf} data-export-ignore="true">
          Export PDF
        </button>
      </div>

      {/* ── Tooth Chart ── */}
      <div className="replay-chart-stage">
        <ChartBig
          toothStates={replayState.states ?? {}}
          surfacesMap={replayState.surfaces ?? {}}
          flagsMap={replayState.flags ?? {}}
          movementsMap={replayState.movements ?? {}}
          selectedTeeth={currentFrame?.tooth ? [currentFrame.tooth] : []}
          onToothClick={() => { }}
          isLocked={true}
          animateReplay={playbackSpeed >= 500}
          fitS={REPLAY_FIT_S}
          title={
            currentFrame?.timestamp
              ? `Dental Chart — ${formatDateTime(currentFrame.timestamp)}`
              : "Dental Chart"
          }
        />
      </div>

      {/* ── Slider / timeline ── */}
      <div className="replay-timeline">
        <div className="replay-slider-container">
          <input
            type="range"
            min={0}
            max={Math.max(0, frames.length - 1)}
            value={currentFrameIndex < 0 ? 0 : currentFrameIndex}
            onChange={handleSliderChange}
            className="replay-slider"
            disabled={frames.length === 0}
          />
          <div className="slider-labels">
            <span className="label-start">{formatTime(frames[0]?.timestamp)}</span>
            <span className="label-current">{formatTime(currentFrame?.timestamp)}</span>
            <span className="label-end">{formatTime(frames[frames.length - 1]?.timestamp)}</span>
          </div>
        </div>
      </div>

      {/* ── Playback controls ── */}
      <div className="replay-controls">
        <button type="button" className="btn-replay-control" onClick={goToFirst}
          disabled={currentFrameIndex === 0} title="First frame">⏮ First</button>
        <button type="button" className="btn-replay-control" onClick={goToPrevious}
          disabled={currentFrameIndex === 0} title="Previous">⏪ Prev</button>
        <button
          type="button"
          className={`btn-replay-control btn-play ${isPlaying ? "playing" : ""}`}
          onClick={togglePlay}
          title={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? "⏸ Pause" : "▶ Play"}
        </button>
        <button type="button" className="btn-replay-control" onClick={goToNext}
          disabled={currentFrameIndex >= frames.length - 1} title="Next">Next ⏩</button>
        <button type="button" className="btn-replay-control" onClick={goToLast}
          disabled={currentFrameIndex >= frames.length - 1} title="Last frame">Last ⏭</button>
      </div>

      {/* ── Frame info strip ── */}
      {currentFrame && (
        <div className="replay-frame-info">
          <div className="frame-details">
            <div className="frame-item">
              <span className="frame-label">Procedure</span>
              <span className="frame-value">
                {(typeof currentFrame.procedure === "object" && currentFrame.procedure?.name)
                  || (typeof currentFrame.procedure === "string" ? currentFrame.procedure : null)
                  || "—"}
              </span>
            </div>
            <div className="frame-item">
              <span className="frame-label">Tooth</span>
              <span className="frame-value">#{currentFrame.tooth || "—"}</span>
            </div>
            <div className="frame-item">
              <span className="frame-label">Event</span>
              <span className="frame-value symbol-badge">{currentFrame.symbol || "—"}</span>
            </div>
            <div className="frame-item">
              <span className="frame-label">Step</span>
              <span className="frame-value">{currentFrame.step_label || currentFrame.step_key || "—"}</span>
            </div>
            <div className="frame-item">
              <span className="frame-label">Time</span>
              <span className="frame-value">{formatDateTime(currentFrame.timestamp)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
